import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Star,
  ShieldCheck,
  Zap,
  MessageCircle,
  Clock,
  CalendarDays,
  Store as StoreIcon,
  ChevronRight,
  BadgeCheck,
  Sparkles,
} from "lucide-react";

import api, { API } from "../lib/api";
import { formatNumber } from "../lib/format";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
};

/**
 * Peta badge kepercayaan. `code` dikirim oleh backend
 * (lihat `_seller_badge` di server.py) supaya aturan penentuan badge hanya
 * hidup di satu tempat — frontend cuma mengurus tampilannya.
 */
const BADGE_STYLES = {
  power: {
    icon: Sparkles,
    className: "border-[#FFD700]/60 text-[#FFD700] bg-[#FFD700]/10",
  },
  trusted: {
    icon: BadgeCheck,
    className: "border-[#00FF66]/60 text-[#00FF66] bg-[#00FF66]/10",
  },
  new: {
    icon: Zap,
    className: "border-[#00F0FF]/50 text-[#00F0FF] bg-[#00F0FF]/10",
  },
  regular: {
    icon: ShieldCheck,
    className: "border-white/25 text-white/70 bg-white/5",
  },
};

/** "< 5 menit" / "~2 jam" / "~1 hari" — bahasa manusia, bukan angka mentah. */
function formatReplyTime(minutes) {
  if (minutes == null) return null;
  if (minutes < 5) return "< 5 menit";
  if (minutes < 60) return `± ${minutes} menit`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `± ${hours} jam`;
  return `± ${Math.round(hours / 24)} hari`;
}

/** "Bergabung 3 bulan lalu" — lebih terasa daripada tanggal mentah. */
function formatMemberSince(days) {
  if (days == null) return null;
  if (days < 30) return `${Math.max(days, 1)} hari lalu`;
  if (days < 365) return `${Math.floor(days / 30)} bulan lalu`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return months ? `${years} thn ${months} bln lalu` : `${years} tahun lalu`;
}

function Stat({ icon: Icon, label, value, accent = "text-white" }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-white/40">
        <Icon className="w-2.5 h-2.5" /> {label}
      </span>
      <span className={`text-sm font-bold leading-tight ${accent}`}>{value}</span>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 bg-white/5" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-white/10 w-1/2" />
          <div className="h-2.5 bg-white/5 w-1/3" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-8 bg-white/5" />
        ))}
      </div>
    </div>
  );
}

/**
 * Kartu kepercayaan seller untuk halaman produk & lelang.
 *
 * Tujuannya menjawab satu pertanyaan yang selalu ada di kepala buyer sebelum
 * transfer: "siapa orang ini, dan kenapa saya harus percaya?". Semua angka di
 * sini berasal dari `/sellers/{id}/summary` — tidak ada nilai yang dikarang di
 * frontend, supaya badge & statistik tidak pernah berbeda antar halaman.
 *
 * Props:
 * - sellerId (wajib): id seller.
 * - fallbackName: nama toko dari dokumen produk, dipakai selagi ringkasan
 *   dimuat atau bila endpoint gagal — kartu tidak pernah tampil kosong.
 * - onChat: handler tombol chat. Bila tidak diisi, tombol disembunyikan.
 */
export default function SellerCard({ sellerId, fallbackName = "", onChat }) {
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sellerId) return;
    let active = true;
    setLoading(true);
    setFailed(false);
    api
      .get(`/sellers/${sellerId}/summary`)
      .then((r) => {
        if (active) setSeller(r.data);
      })
      .catch(() => {
        // Ringkasan gagal dimuat (seller lama / error jaringan). Kartu tetap
        // ditampilkan dalam bentuk minimal supaya buyer tidak kehilangan link
        // ke toko.
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sellerId]);

  if (!sellerId) return null;
  if (loading) return <SkeletonCard />;

  const name = seller?.shop_name || fallbackName || "Toko";
  const storeHref = `/store/${seller?.shop_slug || sellerId}`;
  const badge = seller?.badge;
  const badgeStyle = BADGE_STYLES[badge?.code] || BADGE_STYLES.regular;
  const BadgeIcon = badgeStyle.icon;
  const replyTime = formatReplyTime(seller?.avg_reply_minutes);
  const memberSince = formatMemberSince(seller?.member_days);
  const hasRating = (seller?.review_count || 0) > 0;

  return (
    <div
      data-testid="seller-card"
      data-seller-id={sellerId}
      className="border border-white/10 bg-[#0a0a0a] hover:border-[#00F0FF]/40 transition-colors"
    >
      {/* ---- Identitas ---- */}
      <div className="p-4 flex items-start gap-3">
        <Link to={storeHref} className="shrink-0" aria-label={`Kunjungi toko ${name}`}>
          {seller?.avatar ? (
            <img
              src={resolveImg(seller.avatar)}
              alt={name}
              className="w-14 h-14 object-cover border border-white/15"
            />
          ) : (
            <div className="w-14 h-14 bg-[#00F0FF]/10 border border-[#00F0FF]/40 flex items-center justify-center font-display font-black text-xl text-[#00F0FF]">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={storeHref}
              data-testid="seller-card-name"
              className="font-display font-bold text-white truncate hover:text-[#00F0FF] transition-colors"
            >
              {name}
            </Link>
            {seller?.is_open === false ? (
              <span
                data-testid="seller-card-closed"
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#FF003C]/50 bg-[#FF003C]/10 text-[#FF003C] text-[9px] font-black uppercase tracking-widest"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF003C]" /> Tutup
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#00FF66]/40 bg-[#00FF66]/10 text-[#00FF66] text-[9px] font-black uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" /> Buka
              </span>
            )}
          </div>

          {badge && (
            <span
              data-testid={`seller-badge-${badge.code}`}
              title={badge.description}
              className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold uppercase tracking-widest ${badgeStyle.className}`}
            >
              <BadgeIcon className="w-3 h-3" /> {badge.label}
            </span>
          )}

          <div className="mt-1.5 flex items-center gap-1.5 text-xs">
            {hasRating ? (
              <>
                <Star className="w-3.5 h-3.5 fill-[#FFD700] text-[#FFD700]" />
                <span className="font-bold text-white">{seller.rating.toFixed(1)}</span>
                <span className="text-white/45">
                  ({formatNumber(seller.review_count)} ulasan)
                </span>
              </>
            ) : (
              <span className="text-white/40">Belum ada ulasan</span>
            )}
          </div>
        </div>

        <Link
          to={storeHref}
          className="shrink-0 text-white/40 hover:text-[#00F0FF] transition-colors"
          aria-label="Buka halaman toko"
        >
          <ChevronRight className="w-5 h-5" />
        </Link>
      </div>

      {/* ---- Statistik kepercayaan ----
          Hanya ditampilkan bila ringkasan berhasil dimuat. Metrik yang datanya
          belum ada (mis. seller baru yang belum pernah chat) disembunyikan
          daripada menampilkan "0" yang justru terlihat buruk tanpa konteks. */}
      {!failed && seller && (
        <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 gap-3 border-t border-white/5 pt-3">
          <Stat
            icon={Zap}
            label="Terjual"
            value={formatNumber(seller.total_sold || 0)}
            accent="text-[#00F0FF]"
          />
          {seller.completion_rate != null && (
            <Stat
              icon={ShieldCheck}
              label="Pesanan sukses"
              value={`${seller.completion_rate}%`}
              accent={seller.completion_rate >= 95 ? "text-[#00FF66]" : "text-white"}
            />
          )}
          {replyTime && <Stat icon={MessageCircle} label="Balas chat" value={replyTime} />}
          {seller.total_buyers > 0 && (
            <Stat icon={Star} label="Pembeli" value={formatNumber(seller.total_buyers)} />
          )}
          {seller.operational_hours && (
            <Stat icon={Clock} label="Jam buka" value={seller.operational_hours.replace(" WIB", "")} />
          )}
          {memberSince && <Stat icon={CalendarDays} label="Bergabung" value={memberSince} />}
        </div>
      )}

      {/* ---- Aksi ---- */}
      <div className="px-4 pb-4 flex gap-2">
        <Link
          to={storeHref}
          data-testid="seller-card-visit"
          className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-white/80 text-[11px] uppercase tracking-widest font-bold py-2 transition-colors"
        >
          <StoreIcon className="w-3.5 h-3.5" /> Kunjungi Toko
        </Link>
        {onChat && (
          <button
            type="button"
            onClick={onChat}
            data-testid="seller-card-chat"
            className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-white/80 text-[11px] uppercase tracking-widest font-bold py-2 transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Chat Seller
          </button>
        )}
      </div>
    </div>
  );
}
